// Applies consent choices to every tracking technology.
// Each vendor additionally enforces the production-host gate internally.

import posthog from 'posthog-js';
import { getConsent, onConsentChange, type ConsentChoices } from './consent';
import { initAnalytics, updateGoogleConsent } from './analytics';
import { initPostHog, disablePostHog } from './posthog';
import { initRB2B, isRB2BLoaded } from './rb2b';
import { initLeadfeeder, isLeadfeederLoaded } from './leadfeeder';
import { initTracking } from './track';
import { clearAttributionStorage, initAttribution } from './attribution';

/** Attribution / visit storage is analytics storage — needs a measurement category. */
export function attributionAllowed(c: ConsentChoices = getConsent()) {
  return c.analytics || c.experience_analytics;
}

function apply(c: ConsentChoices, previous: ConsentChoices | null) {
  // Google: tag loads under Advanced Consent Mode; only consent state changes.
  updateGoogleConsent(c);

  // PostHog
  if (c.experience_analytics) {
    const started = initPostHog();
    if (started || (previous && !previous.experience_analytics)) {
      initTracking();
      // Page already rendered before consent — record the current page once.
      if (previous && posthog.__loaded) {
        posthog.capture('$pageview', { $current_url: window.location.href });
      }
    }
  } else {
    disablePostHog();
  }

  if (attributionAllowed(c)) {
    initAttribution();
  } else if (previous && attributionAllowed(previous)) {
    clearAttributionStorage();
  }

  // Business visitor identification
  if (c.business_identification) {
    initRB2B();
    initLeadfeeder();
  } else {
    // Defensive: remove any identification storage left from an earlier opt-in.
    clearIdentificationStorage();
  }
  if (!c.business_identification && previous?.business_identification && (isRB2BLoaded() || isLeadfeederLoaded())) {
    // Third-party identification scripts expose no unload/opt-out API; clear
    // their first-party storage, then reload so they stop running on this page.
    clearIdentificationStorage();
    window.location.reload();
  }
}

export function initConsentRuntime() {
  if (typeof window === 'undefined') return;
  initAnalytics();
  apply(getConsent(), null);
  onConsentChange((next, prev) => apply(next, prev));
}

const ID_PREFIXES = ['_lfa', '_reb2b', 'reb2b', '_rb2b'];
function clearIdentificationStorage() {
  try {
    for (const store of [localStorage, sessionStorage]) {
      Object.keys(store).filter((k) => ID_PREFIXES.some((p) => k.startsWith(p))).forEach((k) => store.removeItem(k));
    }
    const host = window.location.hostname.replace(/^www\./, '');
    document.cookie.split(';').forEach((part) => {
      const name = part.split('=')[0].trim();
      if (ID_PREFIXES.some((p) => name.startsWith(p))) {
        for (const domain of ['', `; domain=.${host}`, `; domain=${window.location.hostname}`]) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
        }
      }
    });
  } catch {
    /* ignore */
  }
}
